import React, { useState } from 'react';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import { useAuth } from 'contexts/auth/auth_context';
import { usePermissions, ModuleEnum, PermissionType } from 'contexts/auth/permission_context';

interface UserCardActionsProps {
  onBlockUser: () => void;
  blockDisabled: boolean;
  userUuid: string;
  openManageTermDialog: () => void;
  usersWithTermValidity: boolean;
  status: boolean;
  isBlocked: boolean;
  onUnblockUser: () => void;
}

const UserCardActions: React.FC<UserCardActionsProps> = ({
  onBlockUser,
  blockDisabled,
  userUuid,
  onUnblockUser,
  openManageTermDialog,
  usersWithTermValidity,
  status,
  isBlocked,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const userPermission = user?.permission?.name;
  const isOperatorOrConselho =
    userPermission?.includes('OPERADOR') ||
    userPermission?.includes('CONSELHEIRO');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const hasAnyAction =
    hasPermission(ModuleEnum.USERS, PermissionType.READ) ||
    (!isOperatorOrConselho && hasPermission(ModuleEnum.USERS, PermissionType.UPDATE)) ||
    (!isBlocked && hasPermission(ModuleEnum.USERS, PermissionType.DELETE)) ||
    (isBlocked && hasPermission(ModuleEnum.USERS, PermissionType.UPDATE)) ||
    (usersWithTermValidity && hasPermission(ModuleEnum.USERS, PermissionType.UPDATE));

  return (
    <Box
      sx={{
        marginLeft: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        paddingRight: '16px',
        paddingTop: '8px',
      }}
    >
      {hasAnyAction && (
        <>
          <Button
            variant='outlined'
            endIcon={<ArrowDropDownIcon />}
            sx={{ color: '#0048B6', border: '1px solid #0048B6', fontSize: '15px' }}
            onClick={handleClick}
          >
            Ações
          </Button>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
            {hasPermission(ModuleEnum.USERS, PermissionType.READ) && (
              <MenuItem
                onClick={() => navigate(ROUTES.USER_VIEW.replace(':uuid', userUuid))}
                sx={{ fontSize: '15px' }}
              >
                Visualizar
              </MenuItem>
            )}
            {!isOperatorOrConselho && hasPermission(ModuleEnum.USERS, PermissionType.UPDATE) && (
              <MenuItem
                onClick={() => navigate(ROUTES.USER_EDIT.replace(':uuid', userUuid))}
                sx={{ fontSize: '15px' }}
              >
                Editar
              </MenuItem>
            )}
            {!isBlocked && hasPermission(ModuleEnum.USERS, PermissionType.DELETE) && (
              <MenuItem
                onClick={() => {
                  handleClose();
                  onBlockUser();
                }}
                sx={{ fontSize: '15px' }}
                disabled={blockDisabled}
              >
                Bloquear
              </MenuItem>
            )}
            {isBlocked && hasPermission(ModuleEnum.USERS, PermissionType.UPDATE) && (
              <MenuItem
                onClick={() => {
                  handleClose();
                  onUnblockUser();
                }}
                sx={{ fontSize: '15px' }}
              >
                Desbloquear
              </MenuItem>
            )}
            {usersWithTermValidity && hasPermission(ModuleEnum.USERS, PermissionType.UPDATE) && (
              <MenuItem
                onClick={() => {
                  handleClose();
                  openManageTermDialog();
                }}
                sx={{ fontSize: '15px' }}
              >
                Gerenciar Vigência
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default UserCardActions;
